import type { HomeyAPI } from 'homey-api';
import { useEffect, useState } from 'react';

import { ToastManager } from '../../../../ToastManager.jsx';

import { useI18n } from '../../../../hooks/useI18nFormatters';
import { useApi } from '../../../../store/SelectedHomeyStore.hooks';

import { glyph } from '../../../../theme/glyph';
import { theme } from '../../../../theme/theme';
import styles from './DeviceUpdatesChangelogDialog.module.css';

import { IconButton } from '../../../../components/buttons/IconButton';
import { ExternalLink } from '../../../../components/common/link/ExernalLink';
import { Dialog } from '../../../../components/overlay/Dialog';

interface DeviceUpdatesChangelogDialogProps {
  deviceId: string;
  availableVersion: string;
  currentVersion: string;
  onClose: () => void;
}

export function DeviceUpdatesChangelogDialog(props: DeviceUpdatesChangelogDialogProps) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const [changelogContent, setChangelogContent] = useState<
    | {
        type: 'text';
        value: string;
      }
    | {
        type: 'url';
        value: string;
      }
    | null
  >(null);

  useEffect(() => {
    if (api == null) {
      return;
    }

    const params = new URLSearchParams({ version: props.availableVersion });
    const callOptions: HomeyAPI.ApiCallOptions = {
      method: 'GET',
      path: `/api/manager/devices/device/${props.deviceId}/firmware/details?${params.toString()}`,
      $timeout: 30000,
    };

    api
      .call<{
        changelog?: string | null;
        changelogUrl?: string | null;
      }>(callOptions)
      .then((result) => {
        const changelog = getString(result.changelog);

        if (changelog != null) {
          setChangelogContent({ type: 'text', value: changelog });
          return;
        }

        const changelogUrl = getString(result.changelogUrl);

        if (changelogUrl != null) {
          setChangelogContent({ type: 'url', value: changelogUrl });
          return;
        }

        setChangelogContent({
          type: 'text',
          value: i18n.messageFormatter('settings.system.deviceUpdatesChangelogUnavailable'),
        });
      })
      .catch((error: unknown) => {
        ToastManager.handleError(error);
        setChangelogContent({
          type: 'text',
          value: i18n.messageFormatter('settings.system.deviceUpdatesChangelogUnavailable'),
        });
      });
  }, [api, i18n, props.deviceId, props.availableVersion]);

  return (
    <Dialog containFocus={true} onClose={props.onClose}>
      <div className={styles.root}>
        <div className={styles.header}>
          <div className={styles.title}>
            {i18n.messageFormatter('settings.system.deviceUpdatesChangelogTitle')}
          </div>

          <IconButton
            className={styles.closeButton}
            url={glyph.core.cross}
            size={theme.icon.size_small}
            color={theme.color.icon_light}
            onPress={props.onClose}
          />
        </div>

        <div className={styles.body}>
          <div className={styles.versionSummary}>
            <div className={styles.summaryRow}>
              {i18n.messageFormatter('settings.system.deviceUpdatesCurrentVersionLabel')}:{' '}
              <span className={styles.summaryValue}>{props.currentVersion ?? '-'}</span>
            </div>
            <div className={styles.summaryRow}>
              {i18n.messageFormatter('settings.system.deviceUpdatesAvailableVersionLabel')}:{' '}
              <span className={styles.summaryValue}>{props.availableVersion ?? '-'}</span>
            </div>
          </div>

          <div className={styles.content}>
            {changelogContent?.type === 'url' ? (
              <ExternalLink className={styles.contentLink} url={changelogContent.value}>
                {i18n.messageFormatter('settings.system.updatesReleaseNotesLabel')}
              </ExternalLink>
            ) : (
              changelogContent?.value
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function getString(value: string | null | undefined) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}
